const PLACEHOLDER_REGEX = /{(\d+)}/g;

let config = {};

export default {
  IGNORE_ALL_OTHER_VALIDATORS: 'IGNORE_ALL_OTHER_VALIDATORS',

  setConfig(userConfig) {
    config = userConfig;
  },

  /**
   * Performs parameterized replacement of tokens within the message with the message arguments.
   * The tokens must be numbered and will be replaced by the message arguments by index.
   * Thus {0} gets the first message argument and {1} get the second.
   *
   * For example "{0} is required" with arguments ['Name'] would become "Name is required"
   *
   * @param {string} msg - The message to perform token formatting on.
   * @param {string[]} msgArgs - The arguments to use to replace the tokens with, in order.
   * @return {string} - The formatted message.
   */
  formatMsgTokens(msg, msgArgs) {
    return msg.replace(PLACEHOLDER_REGEX, (match, index) => (typeof msgArgs[index] === 'undefined' ? match : msgArgs[index]));
  },

  /**
   * Formats a validator message by assembling its name and parameters and doing token replacement
   * via {@link #formatMsgTokens}.
   *
   * @param {Object} validator - The validator whose message we wish to format.
   * @param {string} customMsg - Optional custom message to use in place of the one found on
   *        the validator. Used for custom validator functions which return their message in
   *        the case of validation failure.
   * @param {Object} data - The data object whose validation messages we are formatting
   * @return {string} - Properly formatted message.
   */
  formatValidatorMsg(validator, customMsg, data) {
    // name can be a property that is the name OR a function that returns it to us
    let msgParams = [typeof validator.name === 'function' ? validator.name(data) : validator.name];

    // All parameters contained in param become message parameters
    // This works with single parameters as well as arrays of params, but not objects
    if (typeof validator.param !== 'undefined' && validator.param !== true) {
      msgParams = msgParams.concat(validator.param);
    }

    // TODO need to add the ability to add msgParams to a validator for when we need explicit
    //   parameters for a message; also we will eventually need support for named params

    return this.formatMsgTokens(customMsg || validator.msg, msgParams);
  },

  /**
   * Runs a single validator function on a single attribute of the data and returns a formatted
   * error message if it fails validation and undefined if it passes.
   *
   * @param {Object} validator - The validator object to run against the attribute.
   * @param {string} key - The key of the attribute on the data to validate.
   * @param {Object} data - The data to validate
   * @return {Promise<string | undefined>} An error message if the validation failed
   *  undefined otherwise
   */
  runValidator(validator, key, data) {
    const param = typeof validator.param === 'function' ? validator.param(data) : validator.param;

    return new Promise((resolve, reject) => {
      const result = validator.fn(key, param, data);

      (result instanceof Promise ? result : Promise.resolve(result)).then((validationResult) => {
        /* TODO consider revising how we treat the return values of custom validators as it MUST be
         *  exactly undefined to specify a pass or true exactly and this can be a bit hard to
         *  understand perhaps throw an exception or log something? */

        /* built in validators return true or false to specify a passed, or failed validation.
         * custom ones defined in the validator spec will return an error message if they fail and
         * nothing (undefined) if they pass thus we need to be prepared for both types of result
         * additionally we have a special result that can be used to stop all other validators for
         * an attribute */
        if (validationResult !== true && !(validationResult === null || typeof validationResult === 'undefined') && this.IGNORE_ALL_OTHER_VALIDATORS !== validationResult) {
          // eslint-disable-next-line no-param-reassign
          validationResult = this.formatValidatorMsg(validator, validationResult, data);
        }

        // return an error message if we have one otherwise nothing
        resolve(validationResult === true ? undefined : validationResult);
      }).catch(reject);
    }).catch((error) => {
      console.error(error); // eslint-disable-line no-console
      return undefined;
    });
  },

  /**
   * Runs a group of validators on the data and returns the errors if present
   *
   * @param {Object} data - The data to validate
   * @param {Object} validatorHash - A hash of validators for the data as retrieved by
   *        {@link #getValidators} or equivalent.
   * @return {Object} - A hash containing keyed error messages, null otherwise.
   */
  runValidators(data, validatorHash) {
    const warnings = {};

    return new Promise(resolve => {
      Promise.all(Object.keys(validatorHash).map(key => {
        const validatorSet = validatorHash[key];

        return Promise.all(validatorSet.map(validator => this.runValidator(validator, key, data)))
          .then(messages => messages.reduce((ignore, message) => {
            if (ignore || message === this.IGNORE_ALL_OTHER_VALIDATORS) {
              return true;
            }

            if (message !== null && typeof message !== 'undefined') {
              warnings[key] = warnings[key] || [];
              warnings[key].push(message);

              if (config.stopOnFirstError) {
                return true;
              }
            }

            return false;
          }, false));
      })).then(() => resolve(Object.keys(warnings).length ? warnings : null));
    });
  }
};
